import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../services/api";

const TYPES = [
  { value: "", label: "All imports" },
  { value: "purchase-invoices", label: "Purchase Invoice" },
  { value: "sales-invoices", label: "Sales Invoice" },
  { value: "customers", label: "Customer" },
  { value: "products", label: "Product" },
];

const STATUS_BADGE = { COMPLETED: "badge-green", COMPLETED_WITH_ERRORS: "badge-amber", FAILED: "badge-red", RUNNING: "badge-blue" };
const fmtDateTime = (value) => (value ? new Date(value).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "-");

/**
 * Sync history of the purchase invoice, sales invoice, customer and product imports: when, by whom, rows imported / failed,
 * the error log and the original uploaded file (kept in Azure Blob Storage).
 */
const ImportHistory = () => {
  const [importType, setImportType] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.get("/api/v1/import-history", {
        importType: importType || undefined,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
      });
      setRows(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error(error.message || "Failed to load the sync history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const downloadFile = async (path, name) => {
    try {
      await api.downloadFile(path, {}, name);
    } catch (error) {
      toast.error(error.message || "Download failed");
    }
  };

  return (
    <div className="p-4" style={{ position: "relative" }}>
      {loading ? (
        <div className="loading-overlay">
          <div className="spinner-card">
            <div className="spinner-ring" />
            <div className="spinner-label">Loading sync history...</div>
          </div>
        </div>
      ) : null}
      <div className="page-subtitle" style={{ marginBottom: 12 }}>
        Every import of purchase invoices, sales invoices, customers and products: who imported it and when, how many rows went in or
        failed, the error log of the failed rows and the original file.
      </div>
      <div className="filters-bar">
        <select value={importType} onChange={(e) => setImportType(e.target.value)} aria-label="Import type">
          {TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
        <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} aria-label="From date" />
        <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} aria-label="To date" />
        <button className="primary" onClick={load} disabled={loading}>
          {loading ? "Loading..." : "Show"}
        </button>
      </div>
      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Import Type</th>
              <th>Date &amp; Time</th>
              <th>Imported By</th>
              <th>File</th>
              <th style={{ textAlign: "right" }}>Rows</th>
              <th style={{ textAlign: "right" }}>Imported</th>
              <th style={{ textAlign: "right" }}>Failed</th>
              <th>Status</th>
              <th>Error Log</th>
              <th>Original File</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={10}>
                  <div className="empty-state">
                    <span className="empty-state-title">No imports for these filters</span>
                  </div>
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id}>
                  <td>{row.importTypeLabel}</td>
                  <td>{fmtDateTime(row.importedAt)}</td>
                  <td>{row.importedBy}</td>
                  <td style={{ whiteSpace: "normal", maxWidth: 260 }}>{row.fileName}</td>
                  <td style={{ textAlign: "right" }}>{row.rowCount}</td>
                  <td style={{ textAlign: "right" }}>{row.importedCount}</td>
                  <td style={{ textAlign: "right", color: row.failedCount ? "var(--color-danger, #b42318)" : undefined }}>{row.failedCount}</td>
                  <td>
                    <span className={`badge ${STATUS_BADGE[row.status] || "badge-gray"}`}>{row.status.replace(/_/g, " ").toLowerCase()}</span>
                  </td>
                  <td>
                    {row.hasErrorLog ? (
                      <button className="secondary" onClick={() => downloadFile(`/api/v1/import-history/${row.id}/errors`, `import-errors-${row.id}.xlsx`)}>
                        Download
                      </button>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td>
                    {row.hasFile ? (
                      <button className="secondary" onClick={() => downloadFile(`/api/v1/import-history/${row.id}/file`, row.fileName || `import-${row.id}.xlsx`)}>
                        Download
                      </button>
                    ) : (
                      <span className="page-subtitle" title="Files are kept from now on when Azure storage is configured">
                        Not stored
                      </span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ImportHistory;
